import clsx from 'clsx';
import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import { ColorName, Colors } from '../../../theme';
import type { LiteralUnion } from '../../../types';
import styles from './Tag.module.scss';

extend([a11yPlugin]);

export type Props = {
  /**
   * Content to render inside the label. Generally this is plain text,
   * but other content such as icons or Avatars may also be rendered.
   */
  children: React.ReactNode;
  /**
   * Optional property for overriding styles with CSS-in-JS or defining a custom class on the top-level Select element.
   * Use with caution as the DOM structure of the component may change.
   */
  className?: string;
  /**
   * The size of `Tag`.
   * @default 'small'
   */
  size?: 'x-small' | 'small';
  /**
   * The color variant of the label. There are six standard variants but any design system color can be used.
   * @default 'default'
   * */
  variant?: LiteralUnion<TagVariantName, ColorName>;
};

/**
 * Renders a tag component used for displaying text tags.
 */
export const Tag = ({
  children,
  className,
  size = 'small',
  variant = 'default',
}: Props) => {
  let backgroundColor: ColorName = 'UiPrimaryWeaker';
  let textColor: ColorName = 'TextDefault';

  // If the provided color option is a custom color, calculate the correct text color.
  if (!isTagVariant(variant)) {
    backgroundColor = variant;
    const lightTextContrast = colord(Colors[backgroundColor]).contrast(
      Colors.TextInverted
    );
    const darkTextContrast = colord(Colors[backgroundColor]).contrast(
      Colors.TextStronger
    );
    if (lightTextContrast > darkTextContrast) {
      textColor = 'TextInverted';
    } else {
      textColor = 'TextStronger';
    }
  }

  return (
    <span
      className={clsx(styles.root, className)}
      style={{
        '--zds-internal-tag-background': Colors[backgroundColor],
        '--zds-internal-tag-text': Colors[textColor],
      }}
      data-size={size}
      data-variant={variant}
      data-zds
    >
      {children}
    </span>
  );
};

const tagVariants = [
  'default',
  'success',
  'error',
  'warning',
  'inverted',
  'simple',
] as const;

const tagVariantsSet = new Set(tagVariants);

function isTagVariant(variant: Props['variant']): variant is TagVariantName {
  return tagVariantsSet.has(variant as any);
}

type TagVariantName = (typeof tagVariants)[number];
